import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import SearchTileStackInclusionsList from '../shared/SearchTileStackInclusionsList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { isStandardOccupancy } from 'lib/search/searchUtils'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'

const MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY = 5
const MAX_REGULAR_INCLUSIONS_TO_DISPLAY = 2
const MAX_TOTAL_INCLUSIONS_TO_DISPLAY = MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY + MAX_REGULAR_INCLUSIONS_TO_DISPLAY

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  doNotOpenInclusionsModal?: boolean
}

function HotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
  doNotOpenInclusionsModal = false,
}: Props) {
  const intl = useIntl()
  const { luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const activeInclusionCategories = useActiveInclusionFilters()

  const luxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.luxPlusTileInclusions?.length) {
      return offer.luxPlusTileInclusions
    }
    return luxPlusInclusions
  }, [luxPlusInclusions, offer.luxPlusTileInclusions])

  const [
    featuresType,
    regularSearchTileFeatures,
    regularSearchTileFeaturesTitle,
  ] = useMemo(() => {
    const NoTitle = undefined // urgency label will be used instead

    if (offer.tileInclusions?.length) {
      const tileInclusionsBoosted = reorderTileInclusionsByActiveFilters(offer.tileInclusions, activeInclusionCategories)
      return [
        'inclusions',
        tileInclusionsBoosted,
        offer.tileInclusionsHeading,
      ] as const
    }

    if (offer.lowestPricePackage?.inclusions?.length) {
      const filtered = offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
      const boosted = reorderTileInclusionsByActiveFilters(filtered, activeInclusionCategories)
      return [
        'inclusions',
        boosted,
        NoTitle,
      ] as const
    }

    if (offer.lowestPricePackage?.roomType.amenities.length) {
      return [
        'amenities',
        offer.lowestPricePackage.roomType.amenities,
        NoTitle,
      ] as const
    }

    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    return [
      'amenities',
      pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate'),
      NoTitle,
    ] as const
  }, [offer, activeInclusionCategories])

  const loyaltyCount = useLoyaltyBenefitInclusions(offer).length

  const maxLuxPlusInclusionsToDisplay = useMemo<number>(() => {
    if (!regularSearchTileFeatures?.length) return MAX_TOTAL_INCLUSIONS_TO_DISPLAY
    return Math.max(MAX_TOTAL_INCLUSIONS_TO_DISPLAY - regularSearchTileFeatures.length, MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY)
  }, [regularSearchTileFeatures])

  const maxRegularInclusionsToDisplay = useMemo<number>(() => {
    if (!luxPlusSearchTileInclusions.length) return MAX_TOTAL_INCLUSIONS_TO_DISPLAY
    return Math.max(MAX_TOTAL_INCLUSIONS_TO_DISPLAY - luxPlusSearchTileInclusions.length, MAX_REGULAR_INCLUSIONS_TO_DISPLAY)
  }, [luxPlusSearchTileInclusions])

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    if (doNotOpenInclusionsModal) return

    showModal(<Modal
      title={offer.property.name}
      primaryActionText={intl.formatMessage({
        id: 'yz1jWBepgJQnq1ifNEk6',
        defaultMessage: 'View offer',
        description: 'CTA button in hotel inclusions modal to view the offer',
      })}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        <OfferInclusionsList type="lux-plus" inclusions={luxPlusSearchTileInclusions} />
        <Group direction="vertical" gap={4}>
          {regularSearchTileFeatures && <OfferInclusionsList inclusions={regularSearchTileFeatures} />}
          <LoyaltyInclusions offer={offer} variant="offer-page" />
        </Group>
      </Group>
    </Modal>)
  }, [doNotOpenInclusionsModal, showModal, offer, offerUrl, luxPlusSearchTileInclusions, regularSearchTileFeatures, intl])

  const hasStandardOccupancy = isStandardOccupancy(filters)

  return <Group direction="vertical" verticalAlign="space-between" gap={12} grow noDisplayWhenEmpty>
    <Group direction="vertical" gap={12} noDisplayWhenEmpty>
      {!!luxPlusSearchTileInclusions?.length && <LuxPlusInclusions
        luxPlusInclusions={luxPlusSearchTileInclusions}
        accommodationOffer={offer}
        hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
        showMinStayDescription
        truncateAfter={maxLuxPlusInclusionsToDisplay}
        onShowMoreClick={openAllInclusionsModal}
      />}
      {featuresType === 'inclusions' && (!!regularSearchTileFeatures?.length || loyaltyCount > 0) && <Group direction="vertical" gap={4}>
        <SearchTileStackInclusionsList
          inclusions={regularSearchTileFeatures ?? []}
          inclusionsTitle={regularSearchTileFeaturesTitle}
          truncateAfter={maxRegularInclusionsToDisplay}
          additionalHiddenCount={((regularSearchTileFeatures?.length ?? 0) + loyaltyCount) <= maxRegularInclusionsToDisplay ? 0 : loyaltyCount}
          onShowMoreClick={openAllInclusionsModal}
          // we want to show "Select packages only" label on search tile inclusions heading for SPP offers (which are LPC)
          // see https://aussiecommerce.atlassian.net/browse/HP-1012
          showSelectPackageOnlyLabel={offer.type === OFFER_TYPE_ALWAYS_ON}
        />
        {((regularSearchTileFeatures?.length ?? 0) + loyaltyCount) <= maxRegularInclusionsToDisplay && <LoyaltyInclusions offer={offer} variant="search-tile" />}
      </Group>}
      {featuresType === 'amenities' && <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {!!regularSearchTileFeatures?.length && <SearchTileStackAmenitiesList
          amenities={regularSearchTileFeatures}
          amenitiesTitle={regularSearchTileFeaturesTitle}
        />}
        <LoyaltyInclusions offer={offer} variant="search-tile" />
      </Group>}
    </Group>
    {!hasStandardOccupancy && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
    />}
  </Group>
}

export default HotelSearchTileFeatures
